import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"

import * as ExpoLocation from "expo-location"

export interface LocationCoords {
  latitude: number
  longitude: number
}

interface LocationContextType {
  location: LocationCoords | null
  permissionGranted: boolean
  refreshLocation: () => Promise<LocationCoords | null>
}

const LocationContext = createContext<LocationContextType | undefined>(undefined)

export const LocationProvider: FC<PropsWithChildren> = ({ children }) => {
  const [location, setLocation] = useState<LocationCoords | null>(null)
  const [permissionGranted, setPermissionGranted] = useState(false)
  const hasRequestedPermission = useRef(false)

  const fetchLocation = useCallback(async (): Promise<LocationCoords | null> => {
    console.log("[LOCATION] fetchLocation: calling getCurrentPositionAsync...")
    try {
      const pos = await ExpoLocation.getCurrentPositionAsync({
        accuracy: ExpoLocation.Accuracy.Balanced,
      })
      const coords: LocationCoords = {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
      }
      console.log("[LOCATION] fetchLocation: got position", coords)
      setLocation(coords)
      return coords
    } catch (err) {
      console.warn("[LOCATION] fetchLocation: getCurrentPositionAsync failed:", err)
      return null
    }
  }, [])

  const refreshLocation = useCallback(async (): Promise<LocationCoords | null> => {
    console.log("[LOCATION] refreshLocation called, permissionGranted:", permissionGranted)
    return fetchLocation()
  }, [permissionGranted, fetchLocation])

  useEffect(() => {
    if (hasRequestedPermission.current) return
    hasRequestedPermission.current = true

    const requestAndFetch = async () => {
      console.log("[LOCATION] Checking existing foreground permissions...")
      const existing = await ExpoLocation.getForegroundPermissionsAsync()
      console.log("[LOCATION] Existing permission status:", JSON.stringify(existing))

      if (existing.granted) {
        console.log("[LOCATION] Permission already granted, fetching location...")
        setPermissionGranted(true)
        await fetchLocation()
        return
      }

      if (!existing.canAskAgain) {
        console.warn("[LOCATION] Cannot ask for location permission again (canAskAgain=false).")
        return
      }

      console.log("[LOCATION] Requesting foreground location permission...")
      const result = await ExpoLocation.requestForegroundPermissionsAsync()
      console.log("[LOCATION] Permission request result:", JSON.stringify(result))
      setPermissionGranted(result.granted)

      if (result.granted) {
        console.log("[LOCATION] Permission granted, fetching location...")
        await fetchLocation()
      } else {
        console.warn("[LOCATION] Permission denied by user.")
      }
    }

    requestAndFetch()
  }, [fetchLocation])

  return (
    <LocationContext.Provider value={{ location, permissionGranted, refreshLocation }}>
      {children}
    </LocationContext.Provider>
  )
}

export const useLocation = (): LocationContextType => {
  const ctx = useContext(LocationContext)
  if (!ctx) {
    throw new Error("useLocation must be used within a LocationProvider")
  }
  return ctx
}
